import {
    AccessMeetingTemplateField,
    ConnectionTemplateIdMeetingTemplateField,
    DisableOtherPagesTemplateField,
    ExpiryDaysMeetingTemplateField,
    FeaturedMeetingTemplateField,
    ICreateMeetingTemplateDTO,
    IMeetingsTemplateField,
    IMeetingTemplateDTO,
    LandingPageTemplateField,
    LinksMeetingTemplateField,
    MeetingTemplateAction,
    MeetingTemplateLink,
    MeetingTemplateLinkType,
    NotesMeetingTemplateField,
    PageLabelMeetingTemplateField,
    PageLinkDescriptionTemplateField,
    PageLinkEnabledTemplateField,
    PagePathMeetingTemplateField,
    RecordEnabledMeetingTemplateField,
    ReplayMeetingTemplateField,
    SingleMeetingConnectionsAction,
    SingleMeetingCustomPageAction,
    StatusMeetingTemplateField,
    TopicMeetingTemplateField,
    TrackMeetingTemplateField,
    TypeMeetingTemplateField
} from '../../../../api/blueprints/meeting.template.types';
import { ActionType, BlueprintFieldType, BlueprintType } from '../../../../api/blueprints/blueprint.types';
import { FieldKey } from './meeting-template-form.config';
import { TObject } from '../../../../infrastructure/type-extensions';
import { ConnectionVersion } from '../../../../constants/common.constants';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../global/rich-text-editor/utils/transpiler';

export type Values = TObject<unknown>;

export interface MeetingTemplateValues extends Values {
    accountId: number;
    name: string;
    description?: string;
}

export class MeetingTemplateMapper {
    public static toMeetingTemplateDto(values: MeetingTemplateValues): ICreateMeetingTemplateDTO {
        const dto = { type: BlueprintType.MEETING } as ICreateMeetingTemplateDTO;
        dto.accountId = values.accountId;
        dto.name = values[FieldKey.NAME].trim();
        dto.description = values[FieldKey.DESCRIPTION].trim();
        dto.connectionVersion = values[FieldKey.CONNECTION_VERSION] ? ConnectionVersion.V2 : ConnectionVersion.V1;
        const externalId = (values[FieldKey.EXTERNAL_ID] as string)?.trim();
        dto.externalId = externalId || null;
        dto.fields = MeetingTemplateMapper.toMeetingTemplateFields(values);
        dto.actions = MeetingTemplateMapper.toMeetingTemplateActions(values);

        return dto;
    }

    public static toEditMeetingTemplateDTO(values: MeetingTemplateValues): IMeetingTemplateDTO {
        const dto = MeetingTemplateMapper.toMeetingTemplateDto(values);
        dto.blueprintId = values.blueprintId as string;
        dto.createdAt = values.createdAt as string;

        return dto;
    }

    public static toMeetingTemplateValuesFromMeetingTemplateDTO(dto: IMeetingTemplateDTO): MeetingTemplateValues {
        const values = {} as MeetingTemplateValues;

        values.accountId = dto.accountId;
        values.name = dto.name;
        values.description = dto.description;
        values.createdAt = dto.createdAt;
        values.updatedAt = dto.createdAt;
        values.connectionVersion = dto.connectionVersion === ConnectionVersion.V2;
        values[FieldKey.EXTERNAL_ID] = dto.externalId || '';

        dto.fields?.forEach(field => {
            // Since CKEditor works with classes, but data is stored with inline styles,
            // all CKEditor values need to be converted to the appropriate format
            values[field.name] = this.transpileIfToCKEditor(field.name, field.value);
        });

        dto.actions?.forEach(action => {
            const correctActionKey = MeetingTemplateMapper.actionMapRelations.get(action.type);
            values[correctActionKey as string] = true;
        });

        return values;
    }
    private static toMeetingTemplateFields(values: MeetingTemplateValues): IMeetingsTemplateField[] {
        return MeetingTemplateMapper.fieldMapperList
            .reduce((fields, mapper) => {
                const field = mapper(values);
                return field ? fields.concat(field) : fields;
            }, [] as IMeetingsTemplateField[])
            ;
    }

    private static toMeetingTemplateActions(values: MeetingTemplateValues): MeetingTemplateAction[] {
        return MeetingTemplateMapper.actionMapperList
            .reduce((actions, mapper) => {
                const action = mapper(values);
                return action ? actions.concat(action) : actions;
            }, [] as MeetingTemplateAction[]);
    }

    private static get fieldMapperList(): ((values: MeetingTemplateValues) => IMeetingsTemplateField | null)[] {
        return [
            MeetingTemplateMapper.mapMeetingStatusField,
            MeetingTemplateMapper.mapMeetingAccessField,
            MeetingTemplateMapper.mapMeetingReplayField,
            MeetingTemplateMapper.mapFeaturedMeetingField,
            MeetingTemplateMapper.mapMeetingTypeField,
            MeetingTemplateMapper.mapMeetingTrackField,
            MeetingTemplateMapper.mapMeetingTopicField,
            MeetingTemplateMapper.mapMeetingNotesField,
            MeetingTemplateMapper.mapMeetingLinksField,
            MeetingTemplateMapper.mapPagePathField,
            MeetingTemplateMapper.mapPageLabelField,
            MeetingTemplateMapper.mapPageLinkDescriptionField,
            MeetingTemplateMapper.mapPageLinkEnabledField,
            MeetingTemplateMapper.mapLandingPageField,
            MeetingTemplateMapper.mapDisableOtherPagesField,
            MeetingTemplateMapper.mapConnectionsTemplateField,
            MeetingTemplateMapper.mapMeetingRecordField,
            MeetingTemplateMapper.mapExpiryDaysField,
        ];
    }

    private static get actionMapperList(): ((values: MeetingTemplateValues) => MeetingTemplateAction | null)[] {
        return [
            MeetingTemplateMapper.mapSingleMeetingCustomPageAction,
            MeetingTemplateMapper.mapSingleMeetingConnectionsAction
        ];
    }

    private static get actionMapRelations(): Map<ActionType, FieldKey> {
        return new Map([
            [ActionType.SINGLE_MEETING_CUSTOM_PAGE, FieldKey.SINGLE_MEETING_CUSTOM_PAGE],
            [ActionType.SINGLE_MEETING_CONNECTIONS, FieldKey.APPLY_CONNECTIONS_TEMPLATE]
        ]);
    }

    private static mapMeetingStatusField(values: MeetingTemplateValues): StatusMeetingTemplateField | null {
        if (!values[FieldKey.MEETING_STATUS]) return null;
        return { name: 'status', value: values[FieldKey.MEETING_STATUS] as string, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapMeetingAccessField(values: MeetingTemplateValues): AccessMeetingTemplateField | null {
        if (!values[FieldKey.MEETING_ACCESS]) return null;
        return { name: 'meetingAccess', value: values[FieldKey.MEETING_ACCESS] as string, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapMeetingReplayField(values: MeetingTemplateValues): ReplayMeetingTemplateField {
        if (!values[FieldKey.REPLAY]) return null;
        return { name: 'replayEnabled', value: values[FieldKey.REPLAY] as boolean, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapFeaturedMeetingField(values: MeetingTemplateValues): FeaturedMeetingTemplateField {
        if (!values[FieldKey.FEATURED]) return null;
        return { name: 'featured', value: values[FieldKey.FEATURED] as boolean, type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapMeetingTypeField(values: MeetingTemplateValues): TypeMeetingTemplateField | null {
        if (!values[FieldKey.TYPE]) return null;
        return { name: 'type', value: (values[FieldKey.TYPE] as string).trim(), type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapMeetingTrackField(values: MeetingTemplateValues): TrackMeetingTemplateField | null {
        if (!values[FieldKey.TRACK]) return null;
        return { name: 'track', value: (values[FieldKey.TRACK] as string).trim(), type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapMeetingTopicField(values: MeetingTemplateValues): TopicMeetingTemplateField | null {
        if (!values[FieldKey.TOPIC]) return null;
        return { name: 'topic', value: (values[FieldKey.TOPIC] as string).trim(), type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapMeetingNotesField(values: MeetingTemplateValues): NotesMeetingTemplateField | null {
        if (!values[FieldKey.NOTES]) return null;
        // Since CKEditor works with classes, but data is stored with inline styles,
        // all CKEditor values need to be converted to the appropriate format
        const transpiledValue = transpileClassesToInlineStyles(values[FieldKey.NOTES] as string);
        return { name: 'notes', value: transpiledValue.trimEnd(), type: BlueprintFieldType.FORM_PREFILL };
    }
    private static mapMeetingLinksField(values: MeetingTemplateValues): LinksMeetingTemplateField | null {
        if (!(values.links as MeetingTemplateLink[]).length) return null;
        return {
            name: 'links',
            value: (values.links as MeetingTemplateLink[]).map(link => ({ ...link, type: MeetingTemplateLinkType.Other })),
            type: BlueprintFieldType.FORM_PREFILL
        };
    }
    private static mapPagePathField(values: MeetingTemplateValues): PagePathMeetingTemplateField | null {
        if (!values[FieldKey.PAGE_PATH]) return null;
        return { name: 'path', value: values[FieldKey.PAGE_PATH] as string, type: BlueprintFieldType.SINGLE_MEETING_PAGE_ACTION_INPUT };
    }
    private static mapPageLabelField(values: MeetingTemplateValues): PageLabelMeetingTemplateField | null {
        if (!values[FieldKey.PAGE_LABEL]) return null;
        return { name: 'label', value: (values[FieldKey.PAGE_LABEL] as string).trim(), type: BlueprintFieldType.SINGLE_MEETING_PAGE_ACTION_INPUT };
    }
    private static mapPageLinkDescriptionField(values: MeetingTemplateValues): PageLinkDescriptionTemplateField | null {
        if (!values[FieldKey.LINK_DESCRIPTION]) return null;
        return { name: 'linkDescription', value: (values[FieldKey.LINK_DESCRIPTION] as string).trim(), type: BlueprintFieldType.SINGLE_MEETING_PAGE_ACTION_INPUT };
    }
    private static mapPageLinkEnabledField(values: MeetingTemplateValues): PageLinkEnabledTemplateField | null {
        if (!values[FieldKey.PAGE_LINK_ENABLED]) return null;
        return { name: 'pageLinkEnabled', value: values[FieldKey.PAGE_LINK_ENABLED] as boolean, type: BlueprintFieldType.SINGLE_MEETING_PAGE_ACTION_INPUT };
    }
    private static mapLandingPageField(values: MeetingTemplateValues): LandingPageTemplateField | null {
        if (!values[FieldKey.LANDING_PAGE]) return null;
        return { name: 'landingPage', value: values[FieldKey.LANDING_PAGE] as boolean, type: BlueprintFieldType.SINGLE_MEETING_PAGE_ACTION_INPUT };
    }
    private static mapDisableOtherPagesField(values: MeetingTemplateValues): DisableOtherPagesTemplateField | null {
        if (!values[FieldKey.DISABLE_OTHER_PAGES]) return null;
        return { name: 'disableOtherPages', value: values[FieldKey.DISABLE_OTHER_PAGES] as boolean, type: BlueprintFieldType.SINGLE_MEETING_PAGE_ACTION_INPUT };
    }
    private static mapConnectionsTemplateField(values: MeetingTemplateValues): ConnectionTemplateIdMeetingTemplateField | null {
        if (!values[FieldKey.CONNECTIONS_TEMPLATE]) return null;
        return { name: 'connectionsBlueprintId', value: values[FieldKey.CONNECTIONS_TEMPLATE] as string, type: BlueprintFieldType.SINGLE_MEETING_CONNECTIONS_ACTION_INPUT };
    }

    private static mapSingleMeetingCustomPageAction(values: MeetingTemplateValues): SingleMeetingCustomPageAction | null {
        if (!values[FieldKey.SINGLE_MEETING_CUSTOM_PAGE]) return null;
        return { order: 0, type: ActionType.SINGLE_MEETING_CUSTOM_PAGE };
    }
    private static mapSingleMeetingConnectionsAction(values: MeetingTemplateValues): SingleMeetingConnectionsAction | null {
        if (!values[FieldKey.APPLY_CONNECTIONS_TEMPLATE]) return null;
        return { order: 1, type: ActionType.SINGLE_MEETING_CONNECTIONS };
    }

    private static mapMeetingRecordField(values: MeetingTemplateValues): RecordEnabledMeetingTemplateField | null {
        if (!values[FieldKey.RECORD]) return null;
            return {name: 'recordEnabled', value: values[FieldKey.RECORD] as boolean, type: BlueprintFieldType.FORM_PREFILL
        };
    }

    private static mapExpiryDaysField(values: MeetingTemplateValues): ExpiryDaysMeetingTemplateField | null {
        const days = values[FieldKey.EXPIRY_DAYS] as number | null;
        if (days === null || days <= 0) return null;
        return { name: 'expiryDays', value: days, type: BlueprintFieldType.FORM_PREFILL };
    }

    private static transpileIfToCKEditor = (name: string, value: IMeetingsTemplateField['value']): IMeetingsTemplateField['value'] => {
        if (name === FieldKey.NOTES && typeof value === 'string') {
            return transpileInlineStylesToClasses(value);
        } else {
            return value;
        }
    };
}
